/**
 * Фонды — накопления из ЧП с прогнозом доходности.
 *
 * Логика: для каждого фонда задаём долю % от чистой прибыли (которая
 * автоматически берётся как средняя за последние 3 месяца). На основе current
 * balance, ставки %/год и срока считаем прогноз с компаундом.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Spinner } from "@/components/Spinner";
import { Icon } from "@/components/prototype/Icon";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import {
  fetchFunds, createFund, patchFund, deleteFund,
  fetchFundAllocations, createFundAllocation, deleteFundAllocation,
  fetchFundProjection,
  formatRub,
  type FundRow, type FundAllocationRow,
} from "@/api/finance";
import { ApiError } from "@/api/client";


function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
}

function fmtYears(months: number): string {
  if (months < 12) return `${months} мес`;
  const years = months / 12;
  if (Number.isInteger(years)) return `${years} год${years === 1 ? "" : years < 5 ? "а" : ""}`;
  return `${years.toFixed(1)} лет`;
}


/** Считает помесячный прогноз баланса фонда на клиенте (формула компаунда). */
function projectFund(
  currentBalance: number, monthlyContribution: number,
  annualYieldPct: number, months: number,
): { balance: number; yield: number; cumYield: number }[] {
  const r_m = annualYieldPct / 12 / 100;
  const out: { balance: number; yield: number; cumYield: number }[] = [];
  let balance = currentBalance;
  let cumYield = 0;
  for (let i = 0; i < months; i++) {
    const yieldI = balance * r_m;
    balance = balance + monthlyContribution + yieldI;
    cumYield += yieldI;
    out.push({ balance, yield: yieldI, cumYield });
  }
  return out;
}


/** Smooth Catmull-Rom path для красивых SVG-линий. */
function smoothPath(pts: [number, number][]): string {
  if (pts.length === 0) return "";
  if (pts.length === 1) return `M${pts[0][0]},${pts[0][1]}`;
  let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
  }
  return d;
}

/** Красивый sparkline с gradient-заливкой и smooth-кривой. */
function Sparkline({ data, width = 100, height = 30, color = "var(--green)", id }: {
  data: number[]; width?: number; height?: number; color?: string; id?: string;
}) {
  if (data.length === 0) return null;
  const gradId = id || `spark-grad-${Math.random().toString(36).slice(2, 8)}`;
  const padding = 2;
  const min = Math.min(...data, 0);
  const max = Math.max(...data, 1);
  const range = max - min || 1;
  const dx = data.length > 1 ? (width - padding * 2) / (data.length - 1) : 0;
  const pts: [number, number][] = data.map((v, i) => [
    padding + i * dx,
    height - ((v - min) / range) * (height - padding * 2) - padding,
  ]);
  const linePath = smoothPath(pts);
  const areaPath = `${linePath} L${pts[pts.length - 1][0].toFixed(1)},${height} L${pts[0][0].toFixed(1)},${height} Z`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
         style={{ display: "block", overflow: "visible" }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path d={linePath} stroke={color} strokeWidth={2} fill="none" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r={3} fill={color} stroke="#fff" strokeWidth={1.5} />
    </svg>
  );
}


/** Большой line chart с двумя сериями: баланс + накопленный доход.
 *  history — реальные точки [(date, balance)] до сейчас; projection строится
 *  с current_balance вперёд. Стык между ними — вертикальная пунктирная линия. */
function FundChart({ history, contribution, currentBalance, annualYieldPct, horizon, width = 700, height = 200 }: {
  history: { date: string | null; balance: number }[];
  contribution: number;
  currentBalance: number;
  annualYieldPct: number;
  horizon: number;
  width?: number;
  height?: number;
}) {
  const proj = projectFund(currentBalance, contribution, annualYieldPct, horizon);
  if (proj.length === 0 && history.length === 0) return null;
  // Балансы: история (start с 0 если history пуст) + точка "сейчас" + прогноз
  const hist = history.length > 0 ? [0, ...history.map(h => h.balance)] : [0];
  const balances = [...hist, ...proj.map(p => p.balance)];
  // Точка "сейчас" — индекс на стыке (после last historical)
  const nowIdx = hist.length - 1;
  const cumYields: number[] = balances.map((_, i) => i <= nowIdx ? 0 : (proj[i - nowIdx - 1]?.cumYield || 0));
  const maxVal = Math.max(...balances, 1);
  const paddingX = 60;
  const paddingY = 30;   // место сверху под легенду
  const paddingBottom = 22;
  const w = width - paddingX - 12;
  const h = height - paddingY - paddingBottom;
  const dx = balances.length > 1 ? w / (balances.length - 1) : 0;

  // Nice Y-axis ticks: 0, 500к, 1М, 1.5М, 2М вместо 0, 462к, 923к…
  function niceTicks(mx: number, count: number = 4): number[] {
    if (mx <= 0) return [0];
    const exp = Math.pow(10, Math.floor(Math.log10(mx)));
    const f = mx / exp;
    const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
    const step = (nice * exp) / count;
    const stepExp = Math.pow(10, Math.floor(Math.log10(step)));
    const stepF = step / stepExp;
    const niceStep = stepF <= 1 ? stepExp : stepF <= 2 ? 2 * stepExp : stepF <= 2.5 ? 2.5 * stepExp : stepF <= 5 ? 5 * stepExp : 10 * stepExp;
    const ticks: number[] = [];
    let v = 0;
    while (v <= mx * 1.05) { ticks.push(v); v += niceStep; }
    return ticks;
  }
  const yTicks = niceTicks(maxVal, 4);
  const yScaleMax = yTicks[yTicks.length - 1] || maxVal || 1;

  // Точки координат — масштабируются по yScaleMax (чтобы линии совпадали с tick'ами).
  const ptsB: [number, number][] = balances.map((v, i) => [paddingX + i * dx, paddingY + h - (v / yScaleMax) * h]);
  const ptsY: [number, number][] = cumYields.map((v, i) => [paddingX + i * dx, paddingY + h - (v / yScaleMax) * h]);

  // Разделяем path на историю (solid) и прогноз (dashed)
  const ptsBHist = ptsB.slice(0, nowIdx + 1);
  const ptsBProj = ptsB.slice(nowIdx);
  const pathHist = smoothPath(ptsBHist);
  const pathProj = smoothPath(ptsBProj);
  // Area под всей линией (история + прогноз) — единая
  const pathBalance = smoothPath(ptsB);
  const areaBalance = pathBalance + ` L${ptsB[ptsB.length-1][0].toFixed(1)},${paddingY + h} L${ptsB[0][0].toFixed(1)},${paddingY + h} Z`;
  const pathYield = smoothPath(ptsY.slice(nowIdx));
  // X-координата точки "сейчас" для вертикального разделителя
  const nowX = ptsB[nowIdx] ? ptsB[nowIdx][0] : paddingX;

  const fmt = (v: number) =>
    v >= 1_000_000 ? `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)} млн`
    : v >= 1000   ? `${Math.round(v / 1000)} тыс`
    : `${Math.round(v)}`;

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none"
         style={{ display: "block", background: "var(--card)", borderRadius: 6 }}>
      <defs>
        <linearGradient id="balgrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--green)" stopOpacity={0.4} />
          <stop offset="100%" stopColor="var(--green)" stopOpacity={0} />
        </linearGradient>
      </defs>
      {/* Grid — nice ticks */}
      {yTicks.map((v, i) => {
        const y = paddingY + h - (v / yScaleMax) * h;
        return (
          <g key={i}>
            <line x1={paddingX} y1={y} x2={width - 10} y2={y} stroke="var(--line-soft)" strokeWidth="0.5" strokeDasharray="2,3" />
            <text x={paddingX - 6} y={y + 3} textAnchor="end" fontSize="10" fill="var(--mute)">{fmt(v)} ₽</text>
          </g>
        );
      })}
      {/* Area под балансом (общая) — gradient */}
      <path d={areaBalance} fill="url(#balgrad)" />
      {/* Историческая линия — solid */}
      {pathHist && <path d={pathHist} stroke="var(--green)" strokeWidth={2.5} fill="none" strokeLinejoin="round" strokeLinecap="round" />}
      {/* Прогнозная линия — dashed */}
      {pathProj && <path d={pathProj} stroke="var(--green)" strokeWidth={2} fill="none" strokeLinejoin="round" strokeDasharray="5,4" opacity={0.75} />}
      {/* Линия накопленного дохода (прогноз) */}
      {pathYield && cumYields[cumYields.length-1] > 0 && (
        <path d={pathYield} stroke="#C97A1F" strokeWidth={1.5} fill="none" strokeDasharray="3,3" strokeLinejoin="round" />
      )}
      {/* Вертикаль "сейчас" — только если есть реальная история (больше одной точки) */}
      {nowIdx > 1 && (
        <g>
          <line x1={nowX} y1={paddingY} x2={nowX} y2={paddingY + h} stroke="var(--mute-2)" strokeWidth="1" strokeDasharray="2,3" />
          <text x={nowX} y={paddingY + h + 14} fontSize="9.5" fill="var(--mute)" textAnchor="middle">сейчас</text>
        </g>
      )}
      {/* Точки фактических зачислений */}
      {ptsBHist.slice(1).map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={3} fill="var(--green)" stroke="#fff" strokeWidth={1.5} />
      ))}
      {/* Точка прогноза в конце */}
      <circle cx={ptsB[ptsB.length-1][0]} cy={ptsB[ptsB.length-1][1]} r={3.5} fill="var(--green)" stroke="#fff" strokeWidth={1.5} opacity={0.85} />
      {/* Legend — в правом-верхнем углу, не пересекается с "сейчас" */}
      <g transform={`translate(${width - 10}, 14)`}>
        <g transform="translate(-180, 0)">
          <line x1="0" y1="0" x2="14" y2="0" stroke="var(--green)" strokeWidth={2.5} />
          <text x="20" y="3" fontSize="10" fill="var(--ink-3)">Факт</text>
        </g>
        <g transform="translate(-130, 0)">
          <line x1="0" y1="0" x2="14" y2="0" stroke="var(--green)" strokeWidth={2} strokeDasharray="3,2" opacity={0.75} />
          <text x="20" y="3" fontSize="10" fill="var(--ink-3)">Прогноз</text>
        </g>
        {cumYields[cumYields.length-1] > 0 && (
          <g transform="translate(-65, 0)">
            <line x1="0" y1="0" x2="14" y2="0" stroke="#C97A1F" strokeWidth={1.5} strokeDasharray="3,3" />
            <text x="20" y="3" fontSize="10" fill="var(--ink-3)">Накопл. %</text>
          </g>
        )}
      </g>
      {/* X-axis: старт + +N мес */}
      <text x={paddingX} y={height - 5} fontSize="10" fill="var(--mute)">старт</text>
      <text x={width - 10} y={height - 5} fontSize="10" fill="var(--mute)" textAnchor="end">+{horizon} мес → {fmt(ptsB[ptsB.length-1] ? balances[balances.length-1] : 0)} ₽</text>
    </svg>
  );
}


function CreateFundModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [sharePct, setSharePct] = useState("10");
  const [annualYield, setAnnualYield] = useState("15");
  const [termMonths, setTermMonths] = useState("12");
  const [comment, setComment] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => createFund({
      name: name.trim(),
      share_pct: parseFloat(sharePct.replace(",", ".")) || 0,
      comment: comment.trim() || null,
      annual_yield_pct: parseFloat(annualYield.replace(",", ".")) || 0,
      term_months: parseInt(termMonths, 10) || 0,
    }),
    onSuccess: () => { onCreated(); onClose(); },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card" style={{ maxWidth: 480, width: "92vw", padding: 24, position: "relative" }}
           onClick={e => e.stopPropagation()}>
        <button type="button" onClick={onClose} aria-label="Закрыть"
                style={{ position: "absolute", top: 12, right: 12, border: "none", background: "transparent",
                         color: "var(--mute)", cursor: "pointer", fontSize: 20, padding: 4 }}>×</button>
        <h3 style={{ fontFamily: "var(--serif)", fontSize: 22, fontWeight: 700, marginBottom: 16, color: "var(--ink)" }}>
          Новый фонд
        </h3>
        <form onSubmit={e => { e.preventDefault(); mut.mutate(); }}
              style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
            Название фонда <span style={{ color: "var(--brick)" }}>*</span>
            <input type="text" className="input" required value={name}
                   onChange={e => setName(e.target.value)} placeholder="Например: Резервный фонд"/>
          </label>
          <div className="row row-2">
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
              % от ЧП ежемесячно
              <input type="number" className="input" step="0.1" min="0" max="100"
                     value={sharePct} onChange={e => setSharePct(e.target.value)}/>
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
              Доходность %/год
              <input type="number" className="input" step="0.1" min="0"
                     value={annualYield} onChange={e => setAnnualYield(e.target.value)}/>
            </label>
          </div>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
            Срок размещения, мес
            <input type="number" className="input" step="1" min="0"
                   value={termMonths} onChange={e => setTermMonths(e.target.value)}/>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
            Комментарий
            <textarea className="input" rows={2} value={comment}
                      onChange={e => setComment(e.target.value)}
                      placeholder="Например: вклад в Сбер, ставка 16%"
                      style={{ resize: "vertical", fontFamily: "var(--sans)" }}/>
          </label>
          {err && (
            <div className="alert alert-red"><div className="alert-body">{err}</div></div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <button type="button" className="btn" onClick={onClose} disabled={mut.isPending}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={mut.isPending || !name.trim()}>
              {mut.isPending ? "Создаю…" : "Создать"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function AllocationModal({ fund, onClose, onCreated }: {
  fund: FundRow; onClose: () => void; onCreated: () => void;
}) {
  const [amountStr, setAmountStr] = useState("");
  const [comment, setComment] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => createFundAllocation({
      fund_id: fund.id,
      amount: parseFloat(amountStr.replace(",", ".")) || 0,
      source_kind: "manual",
      allocated_at: date,
      comment: comment.trim() || null,
    }),
    onSuccess: () => { onCreated(); onClose(); },
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card" style={{ maxWidth: 400, width: "92vw", padding: 24, position: "relative" }}
           onClick={e => e.stopPropagation()}>
        <button type="button" onClick={onClose} aria-label="Закрыть"
                style={{ position: "absolute", top: 12, right: 12, border: "none", background: "transparent",
                         color: "var(--mute)", cursor: "pointer", fontSize: 20, padding: 4 }}>×</button>
        <h3 style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 700, marginBottom: 6 }}>
          Зачисление в фонд
        </h3>
        <div style={{ fontSize: 13, color: "var(--mute)", marginBottom: 16 }}>{fund.name}</div>
        <form onSubmit={e => { e.preventDefault(); mut.mutate(); }}
              style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
            Сумма, ₽
            <input type="number" className="input" step="any" required autoFocus
                   value={amountStr} onChange={e => setAmountStr(e.target.value)}/>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
            Дата
            <input type="date" className="input" value={date}
                   onChange={e => setDate(e.target.value)}/>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
            Комментарий
            <input type="text" className="input" value={comment}
                   onChange={e => setComment(e.target.value)}
                   placeholder="Например: депозит Сбер от 24.05"/>
          </label>
          {err && <div className="alert alert-red"><div className="alert-body">{err}</div></div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" className="btn" onClick={onClose} disabled={mut.isPending}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={mut.isPending}>
              {mut.isPending ? "Сохраняю…" : "Зачислить"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function AllocationsList({ fundId }: { fundId: number }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["fund-allocations", fundId],
    queryFn: () => fetchFundAllocations({ fund_id: fundId }),
  });
  const delMut = useMutation({
    mutationFn: (id: number) => deleteFundAllocation(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["fund-allocations", fundId] });
      void qc.invalidateQueries({ queryKey: ["funds"] });
    },
  });
  if (q.isLoading) return <div className="muted" style={{ padding: 8 }}>Загрузка…</div>;
  const items = q.data || [];
  if (!items.length) return (
    <div style={{ padding: 12, color: "var(--mute)", fontSize: 12 }}>
      Зачислений ещё нет.
    </div>
  );
  return (
    <table className="tbl tbl-tight" style={{ width: "100%", fontSize: 12 }}>
      <thead><tr>
        <th style={{ width: 100 }}>Дата</th>
        <th>Комментарий</th>
        <th>Источник</th>
        <th className="num" style={{ width: 110 }}>Сумма</th>
        <th style={{ width: 30 }}/>
      </tr></thead>
      <tbody>
        {items.map((a: FundAllocationRow) => (
          <tr key={a.id}>
            <td className="tabular">{fmtDate(a.allocated_at)}</td>
            <td>{a.comment || <span className="muted">—</span>}</td>
            <td>
              {a.source_kind === "dividend" ? (
                <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 999,
                  background: "rgba(212,167,58,0.18)", color: "#7A4E12",
                  border: "1px solid rgba(212,167,58,0.4)" }}>дивиденд #{a.source_id}</span>
              ) : (
                <span style={{ color: "var(--mute)", fontSize: 11 }}>вручную</span>
              )}
            </td>
            <td className="num tabular strong" style={{ color: a.amount < 0 ? "var(--brick)" : "var(--green)" }}>
              {a.amount > 0 ? "+" : ""}{formatRub(a.amount)}
            </td>
            <td>
              <button className="btn btn-sm" style={{ color: "var(--brick)", padding: "2px 6px" }}
                      onClick={() => {
                        if (confirm("Удалить зачисление?")) delMut.mutate(a.id);
                      }}><Icon name="trash" size={12}/></button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


/** Карточка фонда: hero-баланс + sparkline + 4 stat + actions + expand. */
function FundCard({ f, onAlloc, onChanged }: {
  f: FundRow; onAlloc: () => void; onChanged: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const patchMut = useMutation({
    mutationFn: (body: Parameters<typeof patchFund>[1]) => patchFund(f.id, body),
    onSuccess: () => { onChanged(); },
  });
  const delMut = useMutation({
    mutationFn: () => deleteFund(f.id),
    onSuccess: () => { onChanged(); },
  });
  const editField = (label: string, currentValue: number | string, key: "name" | "share_pct" | "annual_yield_pct" | "term_months" | "comment") => () => {
    const v = window.prompt(label, String(currentValue ?? ""));
    if (v === null) return;
    const num = parseFloat(v.replace(",", "."));
    if (key === "name") patchMut.mutate({ name: v.trim() });
    else if (key === "comment") patchMut.mutate({ comment: v });
    else if (key === "term_months") patchMut.mutate({ term_months: parseInt(v, 10) || 0 });
    else if (!isNaN(num)) patchMut.mutate({ [key]: num } as any);
  };

  const sparkData = [
    0,
    ...(f.balance_history || []).map(h => h.balance),
    ...projectFund(f.current_balance, f.monthly_contribution, f.annual_yield_pct, f.term_months || 12).map(p => p.balance),
  ];

  return (
    <div className="card" style={{
      padding: 0, overflow: "hidden", display: "flex", flexDirection: "column",
      borderRadius: 12, transition: "box-shadow 150ms ease",
      // При раскрытии карточка занимает всю ширину grid-сетки
      gridColumn: expanded ? "1 / -1" : undefined,
      boxShadow: expanded ? "0 4px 16px rgba(0,0,0,0.08)" : undefined,
    }}>
      {/* Шапка */}
      <div style={{ padding: "16px 18px 12px", borderBottom: "1px solid var(--line-soft)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <button type="button" onClick={editField("Название фонда:", f.name, "name")}
                    style={{ background: "none", border: "none", padding: 0, color: "var(--ink)",
                             cursor: "pointer", font: "inherit", textAlign: "left",
                             fontFamily: "var(--serif)", fontSize: 17, fontWeight: 700,
                             letterSpacing: "-0.01em", lineHeight: 1.2 }}>
              {f.name}
            </button>
            <button type="button" onClick={editField("Комментарий:", f.comment || "", "comment")}
                    style={{ display: "block", marginTop: 4, background: "none", border: "none",
                             padding: 0, color: "var(--mute)", cursor: "pointer", font: "inherit",
                             fontSize: 12, textAlign: "left", lineHeight: 1.4 }}>
              {f.comment || <span style={{ fontStyle: "italic" }}>+ комментарий</span>}
            </button>
          </div>
          <button type="button" className="btn btn-sm"
                  onClick={() => setExpanded(v => !v)}
                  title={expanded ? "Свернуть" : "Раскрыть план"}
                  style={{ padding: "4px 10px", fontSize: 13 }}>
            {expanded ? "▴" : "▾"}
          </button>
          <button type="button" className="btn btn-sm" style={{ color: "var(--brick)", padding: "4px 8px" }}
                  onClick={() => {
                    if (confirm(`Удалить фонд «${f.name}»? Все зачисления сохранятся в журнале.`)) {
                      delMut.mutate();
                    }
                  }} title="Удалить фонд"><Icon name="trash" size={12}/></button>
        </div>
      </div>

      {/* Hero: баланс + sparkline (sparkline скрыт в expanded — ниже большой график) */}
      <div style={{
        padding: "16px 18px",
        display: "grid",
        gridTemplateColumns: expanded ? "1fr" : "1fr 1.2fr",
        gap: 16, alignItems: "center",
        background: "linear-gradient(180deg, transparent 0%, rgba(61,122,61,0.03) 100%)",
      }}>
        <div>
          <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600, marginBottom: 4 }}>
            Текущий баланс
          </div>
          <div style={{ fontFamily: "var(--serif)", fontSize: 26, fontWeight: 700, color: "var(--ink)", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
            {formatRub(f.current_balance, { unit: true })}
          </div>
          <div style={{ fontSize: 12, color: "var(--green)", marginTop: 4, fontWeight: 600 }}>
            +{formatRub(f.monthly_yield, { unit: true })} <span style={{ color: "var(--mute)", fontWeight: 400 }}>/мес</span>
            <span style={{ color: "var(--mute)", fontWeight: 400, marginLeft: 12 }}>
              · через {fmtYears(f.term_months || 12)} → <b style={{ color: "var(--green)" }}>{formatRub(f.future_value, { unit: true })}</b>
            </span>
          </div>
        </div>
        {!expanded && (
          <div>
            <Sparkline data={sparkData} width={220} height={60} color="var(--green)" id={`spark-fund-${f.id}`} />
          </div>
        )}
      </div>

      {/* Метрики */}
      <div style={{
        padding: "0 18px 12px",
        display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0,
      }}>
        <MiniStat label="% от ЧП" value={`${f.share_pct}%`}
                  sub={`≈ ${formatRub(f.monthly_contribution)} /мес`}
                  onEdit={editField(`% от ЧП (текущее: ${f.share_pct}%):`, f.share_pct, "share_pct")} />
        <MiniStat label="Доходность" value={`${f.annual_yield_pct}%`} sub="годовых"
                  onEdit={editField(`Доходность %/год:`, f.annual_yield_pct, "annual_yield_pct")} />
        <MiniStat label="Срок" value={fmtYears(f.term_months)} sub={`${f.term_months} мес`}
                  onEdit={editField(`Срок размещения, мес:`, f.term_months, "term_months")} />
        <MiniStat label="Прогноз доход"
                  value={formatRub(f.total_interest, { unit: true })}
                  sub="за весь период" valueColor="var(--green)" />
      </div>

      {/* Footer actions */}
      <div style={{
        padding: "12px 18px", borderTop: "1px solid var(--line-soft)",
        display: "flex", alignItems: "center", gap: 8, background: "var(--paper-2)",
      }}>
        <button type="button" className="btn btn-primary" onClick={onAlloc} style={{ flex: 1 }}>
          + Зачислить деньги
        </button>
      </div>

      {expanded && (
        <div style={{ borderTop: "1px solid var(--line-soft)", background: "var(--paper-2)" }}>
          <ExpandedFundContent fund={f} />
        </div>
      )}
    </div>
  );
}


function MiniStat({ label, value, sub, valueColor, onEdit }: {
  label: string; value: string; sub?: string; valueColor?: string; onEdit?: () => void;
}) {
  const inner = (
    <>
      <div style={{ fontSize: 9.5, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontFamily: "var(--serif)", fontSize: 18, fontWeight: 700, color: valueColor || "var(--ink)", marginTop: 2, lineHeight: 1.15 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 10.5, color: "var(--mute)", marginTop: 2 }}>{sub}</div>}
    </>
  );
  if (onEdit) {
    return (
      <button type="button" onClick={onEdit}
              style={{ background: "none", border: "none", padding: "8px 6px 8px 0",
                       textAlign: "left", cursor: "pointer", color: "inherit", font: "inherit",
                       borderRadius: 4, transition: "background 100ms" }}
              onMouseEnter={e => (e.currentTarget.style.background = "var(--paper-2)")}
              onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
              title="Кликни чтобы изменить">
        {inner}
      </button>
    );
  }
  return <div style={{ padding: "8px 6px 8px 0" }}>{inner}</div>;
}


function ExpandedFundContent({ fund }: { fund: FundRow }) {
  const [tab, setTab] = useState<"projection" | "history">("projection");
  const [horizon, setHorizon] = useState<number>(fund.term_months || 12);
  return (
    <div style={{ padding: "10px 14px" }}>
      <div className="chip-row" style={{ marginBottom: 10 }}>
        <button className={`chip ${tab === "projection" ? "active" : ""}`}
                onClick={() => setTab("projection")}><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="trend" size={12}/>План доходности</span></button>
        <button className={`chip ${tab === "history" ? "active" : ""}`}
                onClick={() => setTab("history")}><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="report" size={12}/>История зачислений</span></button>
        {tab === "projection" && (
          <>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: "var(--mute)", alignSelf: "center", marginRight: 4 }}>Горизонт:</span>
            {[6, 12, 24, 36, 60, 120].map(m => (
              <button key={m} className={`chip ${horizon === m ? "active" : ""}`}
                      onClick={() => setHorizon(m)}>{m < 12 ? `${m}мес` : `${m/12}г`}</button>
            ))}
          </>
        )}
      </div>
      {tab === "projection"
        ? <ProjectionTable fundId={fund.id} months={horizon} />
        : <AllocationsList fundId={fund.id} />}
    </div>
  );
}


function ProjectionTable({ fundId, months }: { fundId: number; months: number }) {
  const q = useQuery({
    queryKey: ["fund-projection", fundId, months],
    queryFn: () => fetchFundProjection(fundId, { months }),
  });
  if (q.isLoading) return <div className="muted" style={{ padding: 8 }}>Считаю прогноз…</div>;
  if (q.error) return <div className="alert alert-red" style={{ margin: 0 }}>
    <div className="alert-body">{(q.error as Error).message}</div></div>;
  const data = q.data!;
  if (data.months.length === 0) return <div className="muted" style={{ padding: 8 }}>Нет данных для прогноза.</div>;

  return (
    <div>
      {/* Итоговые KPI прогноза */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginBottom: 10 }}>
        <div className="kpi" style={{ padding: 10 }}>
          <div className="kpi-label" style={{ fontSize: 10 }}>Сейчас</div>
          <div className="kpi-value" style={{ fontSize: 18 }}>{formatRub(data.current_balance, { unit: true })}</div>
        </div>
        <div className="kpi" style={{ padding: 10 }}>
          <div className="kpi-label" style={{ fontSize: 10 }}>Взнос/мес</div>
          <div className="kpi-value" style={{ fontSize: 18 }}>{formatRub(data.monthly_contribution, { unit: true })}</div>
        </div>
        <div className="kpi" style={{ padding: 10 }}>
          <div className="kpi-label" style={{ fontSize: 10 }}>Всего внесём</div>
          <div className="kpi-value" style={{ fontSize: 18 }}>{formatRub(data.totals.total_contributions, { unit: true })}</div>
        </div>
        <div className="kpi" style={{ padding: 10 }}>
          <div className="kpi-label" style={{ fontSize: 10 }}>Доход за период</div>
          <div className="kpi-value" style={{ fontSize: 18, color: "var(--green)" }}>{formatRub(data.totals.total_yield, { unit: true })}</div>
        </div>
        <div className="kpi" style={{ padding: 10, borderColor: "var(--green)", borderWidth: 2, background: "var(--green-soft)" }}>
          <div className="kpi-label" style={{ fontSize: 10, color: "#1F4F1F" }}>Итого через {data.horizon_months} мес</div>
          <div className="kpi-value" style={{ fontSize: 20, color: "#1F4F1F" }}>{formatRub(data.totals.future_value, { unit: true })}</div>
        </div>
      </div>

      {/* Большой график баланса + накопленного дохода */}
      <div style={{ marginBottom: 10, padding: 8, background: "var(--card)", borderRadius: 6, border: "1px solid var(--line)" }}>
        <FundChart
          history={data.history || []}
          contribution={data.monthly_contribution}
          currentBalance={data.current_balance}
          annualYieldPct={data.annual_yield_pct}
          horizon={data.horizon_months}
          height={200}
        />
      </div>

      {/* Помесячная таблица */}
      <div style={{ maxHeight: 360, overflowY: "auto", background: "var(--card)", borderRadius: 6, border: "1px solid var(--line)" }}>
        <table className="tbl tbl-tight" style={{ width: "100%", fontSize: 12 }}>
          <thead style={{ position: "sticky", top: 0, background: "var(--paper-2)", zIndex: 1 }}>
            <tr>
              <th style={{ width: 60 }}>Месяц</th>
              <th style={{ width: 80 }}>Период</th>
              <th className="num" style={{ width: 110 }}>На начало</th>
              <th className="num" style={{ width: 100 }}>+ Взнос</th>
              <th className="num" style={{ width: 100 }}>+ Доход</th>
              <th className="num" style={{ width: 120 }}>На конец</th>
              <th className="num" style={{ width: 110 }}>Накопл. доход</th>
            </tr>
          </thead>
          <tbody>
            {data.months.map(m => (
              <tr key={m.month_no}>
                <td className="tabular" style={{ color: "var(--mute)" }}>{m.month_no}</td>
                <td className="tabular">{m.ym}</td>
                <td className="num tabular" style={{ color: "var(--mute)" }}>{formatRub(m.opening_balance)}</td>
                <td className="num tabular" style={{ color: m.contribution > 0 ? "var(--ink-3)" : "var(--mute-2)" }}>
                  {m.contribution > 0 ? "+" : ""}{formatRub(m.contribution)}
                </td>
                <td className="num tabular" style={{ color: "var(--green)" }}>+{formatRub(m.yield)}</td>
                <td className="num tabular strong">{formatRub(m.closing_balance)}</td>
                <td className="num tabular" style={{ color: "var(--green)" }}>{formatRub(m.cum_yield)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 8, padding: 8, background: "var(--card)", borderRadius: 6, fontSize: 11, color: "var(--mute)", lineHeight: 1.5 }}>
        Формула: <b>доход_мес = баланс_начало × ({data.annual_yield_pct}%/12)</b>. Закрытие = открытие + взнос + доход.
        Регулярный взнос берётся как <b>share_pct × средняя ЧП за 3 мес</b>. Чтобы изменить — задай другой % фонда или используй override при фиксации дивидендов.
      </div>
    </div>
  );
}


export function FinanceFundsPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [allocFund, setAllocFund] = useState<FundRow | null>(null);

  const q = useQuery({
    queryKey: ["funds"],
    queryFn: () => fetchFunds(),
  });

  if (q.isLoading) return <FinanceLayout><Spinner size="lg" label="Загрузка фондов…" /></FinanceLayout>;
  if (q.error) {
    const err = q.error;
    if (err instanceof ApiError && err.status === 403) {
      return <FinanceLayout><div className="alert alert-amber"><div className="alert-body">Доступ только для админа/руководителя</div></div></FinanceLayout>;
    }
    return <FinanceLayout><div className="alert alert-red"><div className="alert-body">{(err as Error).message}</div></div></FinanceLayout>;
  }

  const data = q.data!;
  const sharePctSum = data.total_share_pct;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["funds"] });
    void qc.invalidateQueries({ queryKey: ["fund-allocations"] });
  };

  return (
    <FinanceLayout>
      <div className="hero-overview" style={{ marginBottom: 20 }}>
        <div>
          <div className="hero-headline">{formatRub(data.total_balance, { unit: true })}</div>
          <div style={{ color: "var(--mute-2)", fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
            Накоплено по {data.items.length} {data.items.length === 1 ? "фонду" : "фондам"}.<br />
            Средняя ЧП за 3 мес: {formatRub(data.monthly_np_base, { unit: true })}/мес → база для расчёта взносов.
          </div>
        </div>
        <div className="hero-stat">
          <div className="hero-stat-label">% от ЧП</div>
          <div className="hero-stat-value" style={{ color: sharePctSum > 100 ? "#FF6B6B" : "#fff" }}>
            {sharePctSum.toFixed(0)}<span className="hero-stat-unit">%</span>
          </div>
          <div style={{ color: "var(--mute-2)", fontSize: 12, marginTop: 6 }}>
            {sharePctSum > 100 ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="warn" size={11}/>Сумма долей &gt; 100%</span> : "распределено в фонды"}
          </div>
        </div>
        <div className="hero-stat">
          <div className="hero-stat-label">Доход в месяц</div>
          <div className="hero-stat-value" style={{ color: "#82E682" }}>
            {formatRub(data.total_monthly_yield, { unit: true })}
          </div>
          <div style={{ color: "var(--mute-2)", fontSize: 12, marginTop: 6 }}>с текущих балансов</div>
        </div>
      </div>

      <div className="card card-tight" style={{ marginBottom: 16, padding: "12px 16px",
        display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1, color: "var(--mute)", fontSize: 12 }}>
          Кликни на любое значение в карточке (название, % от ЧП, доходность, срок) чтобы изменить.
          Кнопка ▾ — раскроет помесячный план + историю.
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          + Новый фонд
        </button>
      </div>

      {data.items.length === 0 ? (
        <div className="card" style={{ textAlign: "center", color: "var(--mute)", padding: 32 }}>
          Фондов ещё нет.{" "}
          <button onClick={() => setShowCreate(true)}
            style={{ background: "none", border: "none", padding: 0, color: "var(--brick)",
                     cursor: "pointer", font: "inherit", textDecoration: "underline" }}>
            Создать первый?
          </button>
        </div>
      ) : (
        <>
          {/* Сетка карточек фондов */}
          <div style={{
            display: "grid", gap: 14,
            gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
          }}>
            {data.items.map(f => (
              <FundCard key={f.id} f={f}
                        onAlloc={() => setAllocFund(f)}
                        onChanged={refresh} />
            ))}
          </div>

          {/* ИТОГО — большая dark feature-карточка */}
          {data.items.length > 1 && (() => {
            const totalCurrent      = data.items.reduce((s, f) => s + f.current_balance, 0);
            const totalMonthlyContr = data.items.reduce((s, f) => s + f.monthly_contribution, 0);
            const totalMonthlyYield = data.items.reduce((s, f) => s + f.monthly_yield, 0);
            const totalFuture       = data.items.reduce((s, f) => s + f.future_value, 0);
            const totalInterest     = data.items.reduce((s, f) => s + f.total_interest, 0);
            const totalSharePct     = data.items.reduce((s, f) => s + f.share_pct, 0);
            const avgYield = data.items.length > 0
              ? data.items.reduce((s, f) => s + f.annual_yield_pct, 0) / data.items.length : 0;
            const maxTerm = Math.max(...data.items.map(f => f.term_months || 12));
            const allDates = new Set<string>();
            for (const f of data.items) for (const h of f.balance_history || []) if (h.date) allDates.add(h.date);
            const histDates = Array.from(allDates).sort();
            const histSums = histDates.map(d => {
              let s = 0;
              for (const f of data.items) {
                const points = (f.balance_history || []).filter(h => h.date && h.date <= d);
                if (points.length > 0) s += points[points.length - 1].balance;
              }
              return s;
            });
            const sumByMonth: number[] = [];
            for (let i = 0; i < maxTerm; i++) sumByMonth.push(0);
            for (const f of data.items) {
              const proj = projectFund(f.current_balance, f.monthly_contribution, f.annual_yield_pct, maxTerm);
              proj.forEach((p, i) => { sumByMonth[i] += p.balance; });
            }
            const sumSparkData = [0, ...histSums, ...sumByMonth];
            return (
              <div style={{
                marginTop: 18, padding: "20px 22px",
                background: "linear-gradient(135deg, #1a1614 0%, #2D2622 100%)",
                color: "#fff", borderRadius: 12, position: "relative", overflow: "hidden",
              }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 24, alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 10, opacity: 0.65, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600, marginBottom: 6 }}>
                      ИТОГО ПО ВСЕМ ФОНДАМ · {data.items.length} {data.items.length === 1 ? "фонд" : "фондов"}
                    </div>
                    <div style={{ fontFamily: "var(--serif)", fontSize: 32, fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1 }}>
                      {formatRub(totalCurrent, { unit: true })}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                      Сегодня
                    </div>
                    <div style={{
                      display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12,
                      marginTop: 18, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.1)",
                    }}>
                      <div>
                        <div style={{ fontSize: 9, opacity: 0.6, textTransform: "uppercase", letterSpacing: 0.5 }}>% от ЧП</div>
                        <div style={{ fontFamily: "var(--serif)", fontSize: 17, fontWeight: 700, marginTop: 2 }}>{totalSharePct.toFixed(1)}%</div>
                        <div style={{ fontSize: 10, opacity: 0.5 }}>≈ {formatRub(totalMonthlyContr)}/мес</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, opacity: 0.6, textTransform: "uppercase", letterSpacing: 0.5 }}>Доход/мес</div>
                        <div style={{ fontFamily: "var(--serif)", fontSize: 17, fontWeight: 700, color: "#82E682", marginTop: 2 }}>
                          {formatRub(totalMonthlyYield, { unit: true })}
                        </div>
                        <div style={{ fontSize: 10, opacity: 0.5 }}>средн.ставка {avgYield.toFixed(1)}%</div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Sparkline data={sumSparkData} width={400} height={110} color="#82E682" id="total-spark" />
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 11, opacity: 0.6 }}>
                      <span>старт</span>
                      <span>через {fmtYears(maxTerm)} → <b style={{ color: "#82E682", opacity: 1 }}>{formatRub(totalFuture, { unit: true })}</b></span>
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4, textAlign: "right" }}>
                      🎯 Заработаем за период: <b style={{ color: "#82E682" }}>{formatRub(totalInterest, { unit: true })}</b>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </>
      )}

      {/* Сводный график всех фондов */}
      {data.items.length > 0 && (() => {
        const maxTerm = Math.max(...data.items.map(f => f.term_months || 12));
        const totalCurrentBalance = data.items.reduce((s, f) => s + f.current_balance, 0);
        const totalMonthlyContr   = data.items.reduce((s, f) => s + f.monthly_contribution, 0);
        const avgYield = data.items.length > 0
          ? data.items.reduce((s, f) => s + f.annual_yield_pct, 0) / data.items.length
          : 0;
        // Унифицированная история всех фондов: для каждой даты — сумма балансов всех фондов на эту дату
        const allDates = new Set<string>();
        for (const f of data.items) for (const h of f.balance_history || []) if (h.date) allDates.add(h.date);
        const histDates = Array.from(allDates).sort();
        const histPoints = histDates.map(d => {
          let s = 0;
          for (const f of data.items) {
            const points = (f.balance_history || []).filter(h => h.date && h.date <= d);
            if (points.length > 0) s += points[points.length - 1].balance;
          }
          return { date: d, balance: s };
        });
        return (
          <div className="card" style={{ marginTop: 16, padding: 16 }}>
            <div className="card-h" style={{ marginBottom: 12 }}>
              <div>
                <h3 className="card-title">Сводный прогноз всех фондов</h3>
                <div className="card-sub">
                  {histPoints.length > 0 && <>{histPoints.length} фактических зачислений · </>}
                  Сумма балансов с компаундом · горизонт {fmtYears(maxTerm)} (максимальный срок)
                </div>
              </div>
            </div>
            <FundChart
              history={histPoints}
              contribution={totalMonthlyContr}
              currentBalance={totalCurrentBalance}
              annualYieldPct={avgYield}
              horizon={maxTerm}
              height={240}
            />
          </div>
        );
      })()}

      {showCreate && <CreateFundModal onClose={() => setShowCreate(false)} onCreated={refresh} />}
      {allocFund && <AllocationModal fund={allocFund} onClose={() => setAllocFund(null)} onCreated={refresh} />}

      <div style={{ marginTop: 16, padding: 12, background: "var(--paper-2)", borderRadius: 8,
        fontSize: 12, color: "var(--mute)", lineHeight: 1.5 }}>
        <b>Как считается прогноз с компаундом:</b> {" "}
        future_value = current × (1+r/12)<sup>n</sup> + взнос × ((1+r/12)<sup>n</sup>−1)/(r/12),
        где r — доходность %/год, n — срок в месяцах, взнос = монтлы-вклад из ЧП.{" "}
        <b>Доход за период</b> = future_value − initial − all_contributions.
      </div>

      <style>{`.ar-row > td { padding: 12px 8px; transition: background 100ms ease; }
              .ar-row:hover > td { background: var(--paper-2); }`}</style>
    </FinanceLayout>
  );
}
